import { useState, useEffect } from 'react';
import { NavigateFunction } from 'react-router-dom';
import { Global, css } from '@emotion/react';

import Header from 'components/header/Header';
import Router from 'components/Router';
import LoadingSpinner from 'components/common/LoadingSpinner';
import BlockUser from 'components/empty/BlockedUser';
import { tokenUpdateInterval, wrapperTokenRefresh } from 'utils/auth/tokenAPI';
import Maintenance from 'components/empty/Maintenance';

import {
  getAccToken,
  jwtDecoder,
  validateToken,
  deleteToken,
  // saveRefreshToken,
  getDeocdeRefreshToken,
  getRefToken,
} from 'utils/auth/tokenUtils';
import { AppWrapper, customScrollForWindow } from './AppStyle';
import { loadOnboading, getSupportRegion } from 'utils/snippet';
import { gtagEmit } from 'utils/eventUtils';
import { AuthAPI, businessAuthAPI, AUTH_REQUEST_TIMEOUT_MS } from 'utils/httpAPI';
import { handleVisibilityChange, lastActionUpdate } from 'utils/auth/sessionManager';
import { register } from 'serviceWorker';
import { handleSWMessage } from 'utils/workerMessageParser';
import FakeRouter from 'components/FakeRouter';
import '@seed-design/stylesheet/global.css';
import { useNavigateRef } from 'hooks/useNavigateRef';
import { store } from 'store';
import { getOidcAccessToken } from 'utils/auth/oidcUtils';
import { removeBaId } from 'utils/localStorage';

register();

function App() {
  const navigateRef = useNavigateRef();
  useEffect(() => {
    lastActionUpdate();
    appInit(navigateRef.current);
  }, [navigateRef]);
  useEffect(() => {
    window.addEventListener('offline', () => {});
    window.addEventListener('online', () => {
      window.location.reload();
    });
  }, []);

  const [maintenanceMsg, setMaintenanceMsg] = useState('');
  useEffect(() => {
    const allowRegion = getSupportRegion();
    AuthAPI.tokenRefresh('', allowRegion)
      .then((res) => {
        const { status } = res.data;
        if (status.code === 'status_maintenance') {
          setMaintenanceMsg(status.message);
        }
      })
      .catch((err) => {
        const res = err.response;
        if (res?.status?.code === 'status_maintenance') {
          setMaintenanceMsg(res.status.message);
        }
      });
    document.addEventListener('visibilitychange', handleVisibilityChange, false);
    window.addEventListener('focus', handleVisibilityChange, false);
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleSWMessage);
    }
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleVisibilityChange);
      if ('serviceWorker' in navigator) {
        navigator?.serviceWorker.removeEventListener('message', handleSWMessage);
      }
    };
  }, []);
  return (
    <AppWrapper>
      <Global
        styles={css`
          ${customScrollForWindow}
        `}
      />
      <Header />
      {maintenanceMsg ? (
        <Maintenance message={maintenanceMsg} />
      ) : (
        <>
          <main className="body">
            <Router />
          </main>
          <LoadingSpinner />
          <BlockUser />
          <FakeRouter />
        </>
      )}
    </AppWrapper>
  );
}

const appInit = async (navigate: NavigateFunction) => {
  try {
    await resolveAuthentication(navigate);
  } finally {
    // 토큰을 확보했든 못했든 인증 판정이 끝났음을 알려, 판정 전 화면과 판정 후 화면을 구분할 수 있게 한다.
    store.dispatch({ type: 'user/setAuthResolved' });
  }
};

const resolveAuthentication = async (navigate: NavigateFunction) => {
  if (window.location.pathname === '/login') return false;

  // 비즈계정 선택 상태는 더 이상 레거시에 저장하지 않으므로 이전에 저장된 값을 지운다.
  removeBaId();

  const oidcAccessToken = getOidcAccessToken();
  if (oidcAccessToken) {
    store.dispatch({ type: 'user/setOidcAccessToken', payload: oidcAccessToken });
    return;
  }
 
  const accessToken = getAccToken();
  const decodedAccToken = jwtDecoder(accessToken); 
  if (decodedAccToken && validateToken(decodedAccToken)) {
    store.dispatch({ type: 'user/setAccessToken', payload: accessToken });
    gtagEmit('user', 'custom_client_id', {
      custom_client_id: decodedAccToken.uid,
    });
    return;
  } 
  
  const refreshToken = getDeocdeRefreshToken();
  if (refreshToken && validateToken(refreshToken)) {
    const encodeRefToken = getRefToken();
    // 갱신이 끝난 뒤에 인증 판정을 완료로 표시해야 토큰 없는 화면이 먼저 그려지지 않는다.
    // 응답이 늦으면 판정만 끝내고 갱신은 계속 기다린다 — 나중에 토큰이 저장되면 Main이 소켓을 연다.
    // 전송 오류로 실패해도 유효한 리프레시 토큰은 남겨 두고, 주기 갱신이 재시도할 수 있게 타이머를 켠다.
    // (토큰이 없으면 Main이 타이머를 켜지 않으므로 여기서 직접 켠다. 중복 호출은 기존 타이머를 정리하므로 안전하다.)
    await withDeadline(wrapperTokenRefresh(encodeRefToken), AUTH_REQUEST_TIMEOUT_MS);
    tokenUpdateInterval();
    return;
  }

  try {
    const res = await businessAuthAPI.getBizToken();
    const { status, data } = res;
    const bizToken = status === 200 ? data.data?.token : undefined;
    if (bizToken) {
      store.dispatch({ type: 'user/setBizToken', payload: bizToken });
    } else {
      // 200이 아닌 2xx이거나 토큰이 없는 응답은 세션 없음과 같이 다룬다. 그대로 두면 토큰 없이 Main이 마운트된다.
      navigateToEntry(navigate);
    }
  } catch (_) {
    navigateToEntry(navigate);
  }
};

// 토큰이 하나도 없는 방문자를 온보딩 또는 로그인 페이지로 보낸다.
const navigateToEntry = (navigate: NavigateFunction) => {
  deleteToken();
  navigate(loadOnboading() ? '/login' : '/onboarding', { replace: true });
};

// 프로미스가 제한 시간 안에 끝나지 않으면 undefined로 먼저 돌아온다. 원래 프로미스는 계속 진행된다.
const withDeadline = <T,>(promise: Promise<T>, ms: number): Promise<T | undefined> =>
  Promise.race([promise, new Promise<undefined>((resolve) => setTimeout(() => resolve(undefined), ms))]);

export default App;
